// client/src/utils/apiCall.js
//
// Thin wrapper around fetchWithAuth for standard JSON CRUD calls.
//
// Automatically:
//   - parses the JSON response body
//   - extracts server error messages
//   - calls notify.error() on failure (unless { silent: true })
//   - throws so callers can still add custom error handling
//
// Use fetchWithAuth directly for:
//   - SSE / streaming endpoints
//   - FormData uploads
//   - any call that needs raw Response access
//
// Usage:
//   import { apiCall } from '../utils/apiCall';
//   const { data, res } = await apiCall('/api/projects', { method: 'POST', body: { name: 'New' } });
//
//   // Silent (no toast on failure):
//   const { data } = await apiCall('/api/analysis/get_topicname', { method: 'POST', body: {...}, silent: true });

import { fetchWithAuth } from '../context/AuthContext';
import { notify } from '../context/NotificationContext';

/**
 * Call an API endpoint with automatic error notification.
 *
 * @param {string} url — full endpoint URL
 * @param {object} [options]
 * @param {string} [options.method]  — HTTP method (defaults to GET)
 * @param {object} [options.body]    — JSON-serialisable request body
 * @param {boolean} [options.silent] — suppress automatic error toast (default false)
 * @param {string} [options.errorTitle] — override the toast title for errors
 * @param {string} [options.errorMessage] — override the toast description for errors
 * @param {boolean} [options.persistentError] — make the error toast persistent (default false)
 * @param {...any} [options.*]       — all other options passed through to fetchWithAuth
 * @returns {Promise<{ data: any, res: Response }>}
 */
export async function apiCall(url, options = {}) {
  const {
    silent = false,
    errorTitle,
    errorMessage,
    persistentError = false,
    body,
    ...fetchOptions
  } = options;

  // Build fetch options
  const fetchOpts = { ...fetchOptions };

  if (body !== undefined) {
    fetchOpts.body = JSON.stringify(body);
    fetchOpts.headers = {
      'Content-Type': 'application/json',
      ...fetchOpts.headers,
    };
  }

  let res;
  try {
    res = await fetchWithAuth(url, fetchOpts);
  } catch (networkErr) {
    // Network-level failure (DNS, connection refused, CORS, etc.)
    if (!silent) {
      notify.error(
        'Network error',
        networkErr.message || 'Unable to reach the server. Please check your connection.',
        { persistent: persistentError }
      );
    }
    throw networkErr;
  }

  // Parse JSON body (even on error — server errors are JSON too)
  let data = null;
  try {
    data = await res.json();
  } catch {
    // Non-JSON response (unusual but possible)
    data = null;
  }

  if (!res.ok) {
    // Build the best error message available
    const msg =
      errorMessage ||
      data?.error ||
      data?.message ||
      `Request failed with status ${res.status}`;

    const title = errorTitle || 'Request failed';

    if (!silent) {
      notify.error(title, msg, { persistent: persistentError });
    }

    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return { data, res };
}

export default apiCall;
